<template>
  <div class="template-designer">
    <!-- 顶部工具栏 -->
    <div class="designer-toolbar">
      <div class="toolbar-left">
        <a-button icon="arrow-left" @click="goBack">返回列表</a-button>
        <a-divider type="vertical" />
        <a-button size="small" @click="handleReset" icon="undo">恢复默认模板</a-button>
      </div>

      <div class="toolbar-center">
        <a-radio-group v-model="paperSize" size="small" @change="onPaperSizeChange">
          <a-radio-button value="A4">A4</a-radio-button>
          <a-radio-button value="A5">A5</a-radio-button>
          <a-radio-button value="B5">B5</a-radio-button>
          <a-radio-button value="custom">自定义</a-radio-button>
        </a-radio-group>

        <template v-if="paperSize === 'custom'">
          <a-input-number v-model="paperWidth" :min="50" :max="500" :step="5" size="small" style="width: 80px" /> mm
          <span style="margin: 0 6px">×</span>
          <a-input-number v-model="paperHeight" :min="50" :max="500" :step="5" size="small" style="width: 80px" /> mm
        </template>

        <a-divider type="vertical" />

        <a-button size="small" icon="zoom-in" @click="zoomIn">+</a-button>
        <a-input-number v-model="zoomLevel" size="small" :min="25" :max="200" :step="25" style="width: 70px" @change="onZoomChange" />
        <a-button size="small" icon="zoom-out" @click="zoomOut">-</a-button>
      </div>

      <div class="toolbar-right">
        <a-input v-model="templateName" placeholder="模板名称" size="small" style="width: 150px; margin-right: 10px" />
        <a-button type="primary" icon="eye" @click="handlePreview">打印预览</a-button>
        <a-button icon="reload" @click="handleRefresh">刷新</a-button>
        <a-divider type="vertical" />
        <a-button type="primary" icon="save" @click="handleSave" :loading="saving">保存</a-button>
        <a-button @click="showTemplateJsonModal = true">模板JSON</a-button>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="designer-main">
      <!-- 左侧字段面板 -->
      <div class="fields-panel">
        <a-collapse v-model="activeKeys" :bordered="false" expandIconPosition="left">
          <a-collapse-panel v-for="group in fieldGroups" :key="group.key" :header="group.title">
            <div class="field-group">
              <div
                v-for="field in group.fields"
                :key="field.key"
                class="field-item"
                :class="{ 'field-group-title': field.isGroup }"
                @click="addFieldToCanvas(field)"
                :draggable="!field.isGroup"
                @dragstart="onFieldDragStart($event, field)"
              >
                {{ field.label }}
              </div>
            </div>
          </a-collapse-panel>
        </a-collapse>
      </div>

      <!-- 设计器容器 -->
      <div class="designer-container">
        <div 
          ref="designerContainer" 
          class="designer-canvas"
          @drop.prevent="onDrop($event)"
          @dragover.prevent
        ></div>
      </div>

      <!-- 右侧属性面板 -->
      <div class="property-panel" v-if="selectedElement">
        <div class="property-header">
          <span>属性设置</span>
          <a-button type="link" size="small" @click="selectedElement = null">关闭</a-button>
        </div>
        <div class="property-content">
          <a-form layout="vertical" size="small">
            <a-form-item label="标题">
              <a-input v-model="selectedElement.options.title" @change="updateElement" />
            </a-form-item>
            <a-form-item label="字段">
              <a-input v-model="selectedElement.options.field" @change="updateElement" />
            </a-form-item>
            <a-form-item label="宽度">
              <a-input-number v-model="selectedElement.options.width" :min="10" :max="500" @change="updateElement" />
            </a-form-item>
            <a-form-item label="高度">
              <a-input-number v-model="selectedElement.options.height" :min="5" :max="500" @change="updateElement" />
            </a-form-item>
            <a-form-item label="左边距">
              <a-input-number v-model="selectedElement.options.left" :min="0" :max="500" @change="updateElement" />
            </a-form-item>
            <a-form-item label="上边距">
              <a-input-number v-model="selectedElement.options.top" :min="0" :max="500" @change="updateElement" />
            </a-form-item>
            <a-form-item label="字体大小">
              <a-input-number v-model="selectedElement.options.fontSize" :min="8" :max="72" @change="updateElement" />
            </a-form-item>
            <a-form-item label="对齐方式">
              <a-select v-model="selectedElement.options.textAlign" @change="updateElement">
                <a-select-option value="left">左对齐</a-select-option>
                <a-select-option value="center">居中</a-select-option>
                <a-select-option value="right">右对齐</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item label="粗体">
              <a-switch v-model="selectedElement.options.fontWeight" checked-children="粗" un-checked-children="细" @change="updateElement" />
            </a-form-item>
            <a-form-item label="隐藏标题">
              <a-switch v-model="selectedElement.options.hideTitle" @change="updateElement" />
            </a-form-item>
            <a-form-item>
              <a-button type="danger" size="small" @click="deleteSelectedElement" block>删除元素</a-button>
            </a-form-item>
          </a-form>
        </div>
      </div>
    </div>

    <!-- 预览模态框 -->
    <a-modal
      :visible="previewVisible"
      title="打印预览"
      :width="900"
      :footer="null"
      @cancel="previewVisible = false">
      <div class="preview-toolbar" style="margin-bottom: 16px;">
        <a-button type="primary" icon="printer" @click="handlePrintPreview">打印</a-button>
      </div>
      <div class="preview-content" ref="previewContent">
        <a-spin :spinning="previewLoading">
          <div v-if="previewHtml" v-html="previewHtml" class="preview-html"></div>
          <a-empty v-else description="暂无预览内容" />
        </a-spin>
      </div>
    </a-modal>

    <!-- 模板JSON模态框 -->
    <a-modal
      :visible="showTemplateJsonModal"
      title="模板JSON"
      :width="800"
      @ok="showTemplateJsonModal = false"
      @cancel="showTemplateJsonModal = false">
      <a-textarea v-model="templateJsonText" :rows="15" readOnly />
      <div style="margin-top: 16px; text-align: right;">
        <a-button @click="copyTemplateJson">复制</a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { getTemplateDetail, createTemplate, updateTemplate, getDefaultTemplateByBillType, previewTemplateWithData } from '@/api/plugin/print-template'
import HiprintService from '@/services/hiprint-service'

// 14种单据类型
const BILL_TYPE_LIST = [
  { value: 'purchase_apply', label: '请购单' },
  { value: 'purchase_order', label: '采购订单' },
  { value: 'purchase_in', label: '采购入库' },
  { value: 'purchase_return', label: '采购退货' },
  { value: 'sales_order', label: '销售订单' },
  { value: 'sales_out', label: '销售出库' },
  { value: 'sales_return', label: '销售退货' },
  { value: 'retail_out', label: '零售出库' },
  { value: 'retail_return', label: '零售退货' },
  { value: 'other_in', label: '其它入库' },
  { value: 'other_out', label: '其它出库' },
  { value: 'assemble', label: '组装单' },
  { value: 'disassemble', label: '拆卸单' },
  { value: 'transfer_out', label: '调拔出库' }
]

// 默认字段映射
const DEFAULT_FIELDS = {
  purchase_apply: ['bill_no', 'bill_date', 'supplier_name', 'warehouse_name', 'remark', 'total_amount'],
  purchase_order: ['bill_no', 'bill_date', 'supplier_name', 'warehouse_name', 'remark', 'total_amount'],
  purchase_in: ['bill_no', 'bill_date', 'supplier_name', 'warehouse_name', 'remark', 'total_amount'],
  purchase_return: ['bill_no', 'bill_date', 'supplier_name', 'warehouse_name', 'remark', 'total_amount'],
  sales_order: ['bill_no', 'bill_date', 'customer_name', 'warehouse_name', 'remark', 'total_amount'],
  sales_out: ['bill_no', 'bill_date', 'customer_name', 'warehouse_name', 'remark', 'total_amount'],
  sales_return: ['bill_no', 'bill_date', 'customer_name', 'warehouse_name', 'remark', 'total_amount'],
  retail_out: ['bill_no', 'bill_date', 'customer_name', 'remark', 'total_amount'],
  retail_return: ['bill_no', 'bill_date', 'customer_name', 'remark', 'total_amount'],
  other_in: ['bill_no', 'bill_date', 'warehouse_name', 'reason', 'remark', 'total_amount'],
  other_out: ['bill_no', 'bill_date', 'warehouse_name', 'reason', 'remark', 'total_amount'],
  assemble: ['bill_no', 'bill_date', 'warehouse_name', 'remark'],
  disassemble: ['bill_no', 'bill_date', 'warehouse_name', 'remark'],
  transfer_out: ['bill_no', 'bill_date', 'from_warehouse', 'to_warehouse', 'remark', 'total_amount']
}

export default {
  name: 'TemplateDesigner',
  data() {
    return {
      templateId: null,
      templateName: '',
      billType: '',
      billTypeList: BILL_TYPE_LIST,
      
      // 纸张设置
      paperSize: 'A4',
      paperWidth: 210,
      paperHeight: 297,
      orientation: 'portrait',
      
      // 缩放
      zoomLevel: 100,
      
      // hiprint 相关
      hiprintTemplate: null,
      
      // 拖拽相关
      draggedField: null,
      
      // 选中元素
      selectedElement: null,
      selectedElementIndex: -1,
      
      // 历史记录
      history: [],
      historyIndex: -1,
      
      // 保存状态
      saving: false,
      
      // 预览状态
      previewVisible: false,
      previewLoading: false,
      previewHtml: '',
      
      // 字段面板
      activeKeys: ['main', 'detail', 'company', 'other'],
      fieldGroups: [
        {
          key: 'main',
          title: '单据主体',
          fields: [
            { key: 'header', label: '单据抬头', type: 'text', isGroup: true },
            { key: 'billDate', label: '单据日期', type: 'text', field: 'billDate' },
            { key: 'billNo', label: '单据编号', type: 'text', field: 'billNo' },
            { key: 'billType', label: '单据类型', type: 'text', field: 'billType' },
            { key: 'remark', label: '备注', type: 'text', field: 'remark' },
            { key: 'operator', label: '制单人', type: 'text', field: 'operator' }
          ]
        },
        {
          key: 'detail',
          title: '单据明细',
          fields: [
            { key: 'table_info', label: '明细表', type: 'table', isGroup: true, field: 'table' }
          ]
        },
        {
          key: 'company',
          title: '公司信息',
          fields: [
            { key: 'companyName', label: '公司名称', type: 'text', field: 'companyName' },
            { key: 'companyAddress', label: '公司地址', type: 'text', field: 'companyAddress' },
            { key: 'companyPhone', label: '公司电话', type: 'text', field: 'companyPhone' },
            { key: 'companyFax', label: '公司传真', type: 'text', field: 'companyFax' }
          ]
        },
        {
          key: 'other',
          title: '其他控件',
          fields: [
            { key: 'text', label: '文本', type: 'text' },
            { key: 'barcode', label: '条形码', type: 'barcode' },
            { key: 'qrcode', label: '二维码', type: 'qrcode' },
            { key: 'image', label: '图片', type: 'image' },
            { key: 'hline', label: '横线', type: 'hline' },
            { key: 'vline', label: '竖线', type: 'vline' },
            { key: 'rect', label: '矩形', type: 'rect' }
          ]
        }
      ],
      
      // 模板JSON弹窗
      showTemplateJsonModal: false,
      templateJsonText: ''
    }
  },
  computed: {
    canUndo() {
      return this.historyIndex > 0
    },
    canRedo() {
      return this.historyIndex < this.history.length - 1
    }
  },
  watch: {
    '$route.query.id'(newId) {
      if (newId) {
        this.templateId = newId
        this.loadTemplate()
      }
    },
    '$route.query.billType'(newBillType) {
      if (newBillType) {
        this.billType = newBillType
        const billTypeName = this.billTypeList.find(item => item.value === newBillType)
        if (billTypeName && !this.templateName) {
          this.templateName = billTypeName.label + '模板'
        }
      }
    },
    showTemplateJsonModal(newVal) {
      if (newVal && this.hiprintTemplate) {
        try {
          const templateJson = this.hiprintTemplate.getJson()
          this.templateJsonText = JSON.stringify(templateJson, null, 2)
        } catch (e) {
          console.warn('获取模板JSON失败:', e)
          this.templateJsonText = '获取模板JSON失败'
        }
      }
    }
  },
  mounted() {
    // 解析 URL 参数
    this.templateId = this.$route.query.id || null
    this.billType = this.$route.query.billType || ''
    
    const billTypeName = this.$route.query.billTypeName
    if (billTypeName && !this.templateName) {
      this.templateName = billTypeName + '模板'
    }
    
    // 初始化设计器
    this.initDesigner()
  },
  beforeDestroy() {
    // 清理资源
    if (this.hiprintTemplate) {
      try {
        // hiprint 可能有清理方法
        this.hiprintTemplate.clear()
      } catch (e) {
        console.warn('清理设计器失败:', e)
      }
    }
  },
  methods: {
    /**
     * 初始化设计器
     */
    async initDesigner() {
      try {
        // 检查 hiprint 是否可用
        if (!HiprintService.isAvailable()) {
          this.$message.error('hiprint 未初始化，请刷新页面重试')
          return
        }
        
        // 加载模板
        let templateJson = null
        if (this.templateId) {
          const res = await getTemplateDetail(this.templateId)
          if (res.code === 200 && res.data) {
            this.processTemplateData(res.data)
            templateJson = this.parseTemplateJson(res.data.templateJson)
          }
        } else if (this.billType) {
          // 加载默认模板
          const res = await getDefaultTemplateByBillType(this.billType)
          if (res.code === 200 && res.data) {
            this.processTemplateData(res.data)
            templateJson = this.parseTemplateJson(res.data.templateJson)
          }
        }
        
        // 如果没有模板，创建默认模板
        if (!templateJson) {
          templateJson = this.createDefaultTemplateJson()
        }
        
        // 设置字段
        this.setFields()
        
        // 启动设计器
        this.startDesigner(templateJson)
        
        // 设置纸张
        this.onPaperSizeChange()
        
      } catch (error) {
        console.error('初始化设计器失败:', error)
        this.$message.error('初始化设计器失败')
      }
    },
    
    /**
     * 处理模板数据
     */
    processTemplateData(data) {
      if (!data) return
      
      this.templateName = data.templateName || this.templateName
      this.billType = data.billType || this.billType
      this.paperSize = data.paperSize || this.paperSize
      this.paperWidth = data.paperWidth || this.paperWidth
      this.paperHeight = data.paperHeight || this.paperHeight
      this.orientation = data.orientation || this.orientation
    },
    
    /**
     * 解析模板 JSON
     */
    parseTemplateJson(templateJson) {
      if (!templateJson) return null
      
      try {
        let json = templateJson
        if (typeof json === 'string') {
          json = JSON.parse(json)
        }
        return json
      } catch (e) {
        console.error('解析模板 JSON 失败:', e)
        return null
      }
    },
    
    /**
     * 创建默认模板 JSON
     */
    createDefaultTemplateJson() {
      return {
        panels: [{
          index: 0,
          name: 1,
          height: this.paperHeight,
          width: this.paperWidth,
          printElements: [
            {
              tid: 'provider',
              options: {
                left: 60,
                top: 15,
                height: 15,
                width: 190,
                testData: '@单据抬头',
                textAlign: 'center',
                title: '单据抬头',
                qid: 'billTitle',
                field: 'billTitle',
                hideTitle: true,
                fontSize: 15
              }
            },
            {
              tid: 'provider',
              options: {
                left: 150,
                top: 45,
                height: 13,
                width: 110,
                testData: '@单据编号',
                title: '单据编号',
                qid: 'billNo',
                field: 'billNo',
                fontSize: 10.5
              }
            },
            {
              tid: 'provider',
              options: {
                left: 20,
                top: 45,
                height: 13,
                width: 100,
                testData: '@单据日期',
                field: 'billDate',
                fontSize: 10.5,
                title: '单据日期',
                qid: 'billDate'
              }
            },
            {
              tid: 'provider',
              options: {
                left: 20,
                top: 70,
                height: 13,
                width: 250,
                testData: '@备注',
                field: 'remark',
                fontSize: 10.5,
                title: '备注',
                qid: 'remark'
              }
            },
            {
              tid: 'table',
              options: {
                left: 20,
                top: 95,
                height: 100,
                width: 170,
                qid: 'table',
                field: 'table',
                fontSize: 10.5,
                columns: [[
                  { width: 20, title: '序号', field: 'rowIndex', checked: true, columnId: 'rowIndex', rowspan: 1, colspan: 1, align: 'center' },
                  { width: 30, title: '条码', field: 'barcode', checked: true, columnId: 'barcode', rowspan: 1, colspan: 1 },
                  { width: 50, title: '名称', field: 'itemName', checked: true, columnId: 'itemName', rowspan: 1, colspan: 1 },
                  { width: 30, title: '规格', field: 'spec', checked: true, columnId: 'spec', rowspan: 1, colspan: 1 },
                  { width: 25, title: '单位', field: 'unit', checked: true, columnId: 'unit', rowspan: 1, colspan: 1 },
                  { width: 30, title: '数量', field: 'qty', checked: true, columnId: 'qty', rowspan: 1, colspan: 1, tableSummary: 'sum' },
                  { width: 30, title: '单价', field: 'price', checked: false, columnId: 'price', rowspan: 1, colspan: 1 },
                  { width: 35, title: '金额', field: 'amount', checked: false, columnId: 'amount', rowspan: 1, colspan: 1 }
                ]]
              }
            }
          ],
          paperNumberLeft: 200,
          paperNumberTop: 270
        }]
      }
    },
    
    /**
     * 设置可用字段
     */
    setFields() {
      if (!this.billType) return
      
      const fields = DEFAULT_FIELDS[this.billType] || []
      const fieldObjects = fields.map(field => ({
        field: field,
        name: this.getFieldName(field),
        type: this.getFieldType(field)
      }))
      
      if (this.hiprintTemplate && this.hiprintTemplate.setFields) {
        this.hiprintTemplate.setFields(fieldObjects)
      }
    },
    
    /**
     * 获取字段名称
     */
    getFieldName(field) {
      const fieldNames = {
        'bill_no': '单据编号',
        'bill_date': '单据日期',
        'supplier_name': '供应商名称',
        'customer_name': '客户名称',
        'warehouse_name': '仓库名称',
        'from_warehouse': '调出仓库',
        'to_warehouse': '调入仓库',
        'remark': '备注',
        'total_amount': '总金额',
        'total_quantity': '总数量',
        'reason': '原因'
      }
      return fieldNames[field] || field
    },
    
    /**
     * 获取字段类型
     */
    getFieldType(field) {
      if (field.includes('amount') || field.includes('quantity')) return 'number'
      if (field.includes('date')) return 'date'
      return 'text'
    },
    
    /**
     * 启动设计器
     */
    startDesigner(templateJson) {
      try {
        const container = this.$refs.designerContainer
        if (!container) {
          console.error('设计器容器不存在')
          return
        }
        
        // 确保模板 JSON 有效
        let validTemplateJson = templateJson
        if (!validTemplateJson || !validTemplateJson.panels) {
          console.warn('模板 JSON 无效，使用默认模板')
          validTemplateJson = this.createDefaultTemplateJson()
        }
        
        // 创建模板实例
        console.log('创建模板实例，模板 JSON:', validTemplateJson)
        this.hiprintTemplate = HiprintService.createTemplate(validTemplateJson)
        
        if (!this.hiprintTemplate) {
          throw new Error('创建模板实例失败')
        }
        
        console.log('模板实例创建成功，开始设计')
        
        // 启动设计器
        const designOptions = {
          paperTitle: this.templateName || '打印模板',
          paperWidth: this.paperWidth,
          paperHeight: this.paperHeight
        }
        
        console.log('设计选项:', designOptions)
        this.hiprintTemplate.design(container, designOptions)
        
        console.log('设计器启动成功')
        
        // 添加历史记录
        this.pushHistory()
        
        // 设置元素选择监听
        this.$nextTick(() => {
          this.setupElementSelection()
        })
        
      } catch (error) {
        console.error('启动设计器失败:', error)
        // 不显示错误消息，因为可能是第一次加载
        // this.$message.error('启动设计器失败: ' + error.message)
      }
    },
    
    /**
     * 加载模板
     */
    async loadTemplate() {
      try {
        const res = await getTemplateDetail(this.templateId)
        if (res.code === 200 && res.data) {
          this.processTemplateData(res.data)
          const templateJson = this.parseTemplateJson(res.data.templateJson)
          if (templateJson) {
            this.startDesigner(templateJson)
          }
        }
      } catch (error) {
        console.error('加载模板失败:', error)
        this.$message.error('加载模板失败')
      }
    },
    
    /**
     * 保存模板
     */
    async handleSave() {
      try {
        if (!this.hiprintTemplate) {
          this.$message.error('设计器未启动')
          return
        }
        
        this.saving = true
        
        // 获取模板 JSON
        const templateJson = this.hiprintTemplate.getJson()
        
        // 构建保存数据
        const saveData = {
          id: this.templateId,
          templateName: this.templateName,
          billType: this.billType,
          templateJson: JSON.stringify(templateJson),
          paperSize: this.paperSize,
          paperWidth: this.paperWidth,
          paperHeight: this.paperHeight,
          orientation: this.orientation
        }
        
        let res
        if (this.templateId) {
          // 更新现有模板
          res = await updateTemplate(this.templateId, saveData)
        } else {
          // 创建新模板
          res = await createTemplate(saveData)
        }
        
        if (res.code === 200) {
          this.templateId = (res.data && res.data.id) || this.templateId
          this.$message.success('保存成功')
          this.pushHistory()
        } else {
          this.$message.error(res.msg || '保存失败')
        }
        
      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败: ' + error.message)
      } finally {
        this.saving = false
      }
    },
    
    /**
     * 预览模板
     */
    async handlePreview() {
      try {
        if (!this.hiprintTemplate) {
          this.$message.error('设计器未启动')
          return
        }
        
        this.previewVisible = true
        this.previewLoading = true
        this.previewHtml = ''
        
        // 获取模板 JSON
        const templateJson = this.hiprintTemplate.getJson()
        
        // 使用测试数据
        const testData = this.getTestData()
        
        // 渲染预览
        const htmlElements = this.hiprintTemplate.getHtml(testData)
        if (htmlElements && htmlElements[0]) {
          this.previewHtml = htmlElements[0].innerHTML
        } else {
          this.previewHtml = HiprintService.renderHtml(templateJson, testData)
        }
        
      } catch (error) {
        console.error('预览失败:', error)
        this.$message.error('预览失败: ' + error.message)
      } finally {
        this.previewLoading = false
      }
    },
    
    /**
     * 获取测试数据
     */
    getTestData() {
      return {
        billTitle: '请购单',
        billNo: 'QGD20240101001',
        billDate: '2024-01-01',
        billType: '请购单',
        companyName: '忙果ERP系统有限公司',
        companyAddress: '北京市朝阳区测试路123号',
        companyPhone: '010-12345678',
        companyFax: '010-87654321',
        remark: '这是一个备注信息',
        operator: '管理员',
        supplierName: '测试供应商有限公司',
        customerName: '测试客户有限公司',
        warehouseName: '主仓库',
        items: [
          { rowIndex: 1, barcode: 'SKU001', itemName: '商品A', spec: '规格A', unit: '个', qty: 10, price: 100, amount: 1000 },
          { rowIndex: 2, barcode: 'SKU002', itemName: '商品B', spec: '规格B', unit: '箱', qty: 20, price: 200, amount: 4000 },
          { rowIndex: 3, barcode: 'SKU003', itemName: '商品C', spec: '规格C', unit: '件', qty: 30, price: 166.67, amount: 5000 }
        ]
      }
    },
    
    /**
     * 打印预览
     */
    handlePrintPreview() {
      if (!this.hiprintTemplate) {
        this.$message.error('设计器未启动')
        return
      }
      
      const testData = this.getTestData()
      this.hiprintTemplate.print(testData)
    },
    
    /**
     * 撤销
     */
    handleUndo() {
      if (this.hiprintTemplate && this.canUndo) {
        try {
          this.hiprintTemplate.undo()
          this.historyIndex--
        } catch (e) {
          console.warn('撤销失败:', e)
        }
      }
    },
    
    /**
     * 重做
     */
    handleRedo() {
      if (this.hiprintTemplate && this.canRedo) {
        try {
          this.hiprintTemplate.redo()
          this.historyIndex++
        } catch (e) {
          console.warn('重做失败:', e)
        }
      }
    },
    
    /**
     * 重置
     */
    handleReset() {
      this.$confirm({
        title: '确认重置',
        content: '确定要重置模板吗？所有未保存的修改将丢失。',
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          const templateJson = this.createDefaultTemplateJson()
          this.startDesigner(templateJson)
        }
      })
    },
    
    /**
     * 纸张大小变化
     */
    onPaperSizeChange() {
      const sizes = {
        'A4': { width: 210, height: 297 },
        'A5': { width: 148, height: 210 },
        'B5': { width: 176, height: 250 }
      }
      
      if (sizes[this.paperSize]) {
        this.paperWidth = sizes[this.paperSize].width
        this.paperHeight = sizes[this.paperSize].height
      }
      
      // 更新设计器纸张
      if (this.hiprintTemplate && this.hiprintTemplate.setPaper) {
        const width = this.orientation === 'landscape' ? this.paperHeight : this.paperWidth
        const height = this.orientation === 'landscape' ? this.paperWidth : this.paperHeight
        this.hiprintTemplate.setPaper(width, height)
      }
    },
    
    /**
     * 缩放变化
     */
    onZoomChange() {
      if (this.hiprintTemplate && this.hiprintTemplate.zoom) {
        this.hiprintTemplate.zoom(this.zoomLevel / 100)
      }
    },
    
    /**
     * 单据类型变化
     */
    onBillTypeChange() {
      this.setFields()
    },
    
    /**
     * 返回列表
     */
    goBack() {
      this.$router.push('/print-template/list')
    },
    
    /**
     * 添加历史记录
     */
    pushHistory() {
      if (this.hiprintTemplate) {
        try {
          const json = this.hiprintTemplate.getJson()
          this.history = this.history.slice(0, this.historyIndex + 1)
          this.history.push(JSON.stringify(json))
          this.historyIndex = this.history.length - 1
        } catch (e) {
          console.warn('添加历史记录失败:', e)
        }
      }
    },
    
    /**
     * 放大
     */
    zoomIn() {
      this.zoomLevel = Math.min(200, this.zoomLevel + 25)
      this.onZoomChange()
    },
    
    /**
     * 缩小
     */
    zoomOut() {
      this.zoomLevel = Math.max(25, this.zoomLevel - 25)
      this.onZoomChange()
    },
    
    /**
     * 刷新
     */
    handleRefresh() {
      this.$confirm({
        title: '确认刷新',
        content: '刷新后将重新加载模板，未保存的修改将丢失。',
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          this.initDesigner()
        }
      })
    },
    
    /**
     * 全选
     */
    handleSelectAll() {
      if (this.hiprintTemplate) {
        try {
          this.hiprintTemplate.selectAll()
        } catch (e) {
          console.warn('全选失败:', e)
        }
      }
    },
    
    /**
     * 清空
     */
    handleClear() {
      this.$confirm({
        title: '确认清空',
        content: '清空后将删除所有元素，此操作不可恢复。',
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          const templateJson = this.createDefaultTemplateJson()
          this.startDesigner(templateJson)
        }
      })
    },
    
    /**
     * 添加字段到画布
     */
    addFieldToCanvas(field) {
      if (!this.hiprintTemplate) {
        this.$message.warning('请先加载模板')
        return
      }
      
      try {
        const element = this.createElementByField(field)
        if (element) {
          this.hiprintTemplate.addPrintElement(element)
          this.pushHistory()
        }
      } catch (e) {
        console.error('添加字段失败:', e)
        this.$message.error('添加字段失败: ' + e.message)
      }
    },
    
    /**
     * 根据字段创建元素
     */
    createElementByField(field) {
      const typeMap = {
        'text': 'provider',
        'date': 'provider',
        'number': 'provider',
        'table': 'table',
        'barcode': 'barcode',
        'qrcode': 'qrcode',
        'image': 'image',
        'hline': 'line',
        'vline': 'line',
        'rect': 'rect',
        'ellipse': 'ellipse'
      }
      
      const tid = typeMap[field.type] || 'provider'
      const baseOptions = {
        left: 100,
        top: 100,
        width: 100,
        height: 22,
        title: field.label || field.key,
        testData: field.field ? '@' + field.field : field.label,
        field: field.field || field.key,
        qid: field.key
      }
      
      switch (field.type) {
        case 'table':
          return {
            tid: 'table',
            options: {
              left: 20,
              top: 120,
              width: 170,
              height: 100,
              title: field.label || '明细表',
              field: field.field || 'table',
              qid: field.key || 'table',
              fontSize: 10.5,
              columns: [[
                { width: 20, title: '序号', field: 'rowIndex', checked: true, columnId: 'rowIndex', rowspan: 1, colspan: 1, align: 'center' },
                { width: 30, title: '条码', field: 'barcode', checked: true, columnId: 'barcode', rowspan: 1, colspan: 1 },
                { width: 50, title: '名称', field: 'itemName', checked: true, columnId: 'itemName', rowspan: 1, colspan: 1 },
                { width: 30, title: '规格', field: 'spec', checked: true, columnId: 'spec', rowspan: 1, colspan: 1 },
                { width: 25, title: '单位', field: 'unit', checked: true, columnId: 'unit', rowspan: 1, colspan: 1 },
                { width: 30, title: '数量', field: 'qty', checked: true, columnId: 'qty', rowspan: 1, colspan: 1, tableSummary: 'sum' },
                { width: 30, title: '单价', field: 'price', checked: false, columnId: 'price', rowspan: 1, colspan: 1 },
                { width: 35, title: '金额', field: 'amount', checked: false, columnId: 'amount', rowspan: 1, colspan: 1 }
              ]]
            }
          }
        case 'barcode':
          baseOptions.width = 150
          baseOptions.height = 50
          break
        case 'qrcode':
          baseOptions.width = 80
          baseOptions.height = 80
          break
        case 'image':
          baseOptions.width = 100
          baseOptions.height = 100
          break
        case 'hline':
          baseOptions.width = 200
          baseOptions.height = 0
          baseOptions.lineType = 'horizontal'
          break
        case 'vline':
          baseOptions.width = 0
          baseOptions.height = 100
          baseOptions.lineType = 'vertical'
          break
        case 'rect':
          baseOptions.width = 100
          baseOptions.height = 60
          break
        case 'ellipse':
          baseOptions.width = 100
          baseOptions.height = 60
          break
      }
      
      return { tid, options: baseOptions }
    },
    
    /**
     * 拖拽开始
     */
    onFieldDragStart(event, field) {
      if (field.isGroup) {
        event.preventDefault()
        return
      }
      this.draggedField = field
      event.dataTransfer.effectAllowed = 'copy'
      event.dataTransfer.setData('text/plain', JSON.stringify(field))
    },
    
    /**
     * 拖放到画布
     */
    onDrop(event) {
      try {
        const fieldData = event.dataTransfer.getData('text/plain')
        if (!fieldData) return
        
        const field = JSON.parse(fieldData)
        const element = this.createElementByField(field)
        if (element && this.hiprintTemplate) {
          this.hiprintTemplate.addPrintElement(element)
          this.pushHistory()
        }
      } catch (e) {
        console.error('拖放失败:', e)
      }
      this.draggedField = null
    },
    
    /**
     * 更新元素属性
     */
    updateElement() {
      if (!this.hiprintTemplate || !this.selectedElement) return
      try {
        this.pushHistory()
      } catch (e) {
        console.warn('更新元素失败:', e)
      }
    },
    
    /**
     * 删除选中的元素
     */
    deleteSelectedElement() {
      if (!this.hiprintTemplate || this.selectedElementIndex < 0) return
      
      try {
        const templateJson = this.hiprintTemplate.getJson()
        if (templateJson.panels && templateJson.panels[0]) {
          templateJson.panels[0].printElements.splice(this.selectedElementIndex, 1)
          this.selectedElement = null
          this.selectedElementIndex = -1
          
          // 重新启动设计器
          this.hiprintTemplate = HiprintService.createTemplate(templateJson)
          this.startDesigner(templateJson)
          this.$message.success('已删除元素')
        }
      } catch (e) {
        console.error('删除元素失败:', e)
        this.$message.error('删除元素失败')
      }
    },
    
    /**
     * 监听画布元素点击事件
     */
    setupElementSelection() {
      if (!this.hiprintTemplate || !this.$refs.designerContainer) return
      
      const container = this.$refs.designerContainer
      
      // 使用事件委托监听元素点击
      container.addEventListener('click', (e) => {
        const target = e.target
        // 查找被点击的元素是否是设计元素
        const elementEl = target.closest('[class*="hiprint-"]')
        if (elementEl) {
          this.findAndSelectElement(elementEl)
        } else {
          this.selectedElement = null
          this.selectedElementIndex = -1
        }
      })
    },
    
    /**
     * 查找并选中元素
     */
    findAndSelectElement(elementEl) {
      if (!this.hiprintTemplate || !this.$refs.designerContainer) return
      
      try {
        const templateJson = this.hiprintTemplate.getJson()
        if (!templateJson.panels || !templateJson.panels[0]) return
        
        const elements = templateJson.panels[0].printElements
        const rect = elementEl.getBoundingClientRect()
        const container = this.$refs.designerContainer
        const elRect = container.getBoundingClientRect()
        
        // 通过位置匹配元素
        for (let i = 0; i < elements.length; i++) {
          const el = elements[i]
          const left = elRect.left + (el.options.left || 0)
          const top = elRect.top + (el.options.top || 0)
          const width = el.options.width || 100
          const height = el.options.height || 22
          
          if (rect.left >= left - 5 && rect.left <= left + width + 5 &&
              rect.top >= top - 5 && rect.top <= top + height + 5) {
            this.selectedElement = el
            this.selectedElementIndex = i
            return
          }
        }
        
        // 如果位置不匹配，尝试通过DOM查找
        const hiprintElements = container.querySelectorAll('[class*="hiprint-printElement"]')
        for (let i = 0; i < hiprintElements.length; i++) {
          if (hiprintElements[i] === elementEl || hiprintElements[i].contains(elementEl)) {
            if (elements[i]) {
              this.selectedElement = elements[i]
              this.selectedElementIndex = i
            }
            return
          }
        }
      } catch (e) {
        console.warn('选择元素失败:', e)
      }
    },
    
    /**
     * 复制模板JSON
     */
    copyTemplateJson() {
      if (this.templateJsonText) {
        navigator.clipboard.writeText(this.templateJsonText).then(() => {
          this.$message.success('已复制到剪贴板')
        }).catch(() => {
          this.$message.error('复制失败')
        })
      }
    }
  }
}
</script>

<style scoped>
.template-designer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;
}

.designer-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.toolbar-left,
.toolbar-center,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-label {
  color: #666;
  font-size: 13px;
}

.designer-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧字段面板 */
.fields-panel {
  width: 200px;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  overflow-y: auto;
  flex-shrink: 0;
}

.fields-panel >>> .ant-collapse {
  background: transparent;
}

.fields-panel >>> .ant-collapse-item {
  border-bottom: 1px solid #f0f0f0;
}

.fields-panel >>> .ant-collapse-header {
  padding: 10px 16px;
  font-weight: 500;
  color: #333;
  background: #fafafa;
}

.fields-panel >>> .ant-collapse-content-box {
  padding: 8px 16px;
}

.field-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.field-item {
  padding: 4px 10px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  font-size: 12px;
  color: #1890ff;
  cursor: move;
  transition: all 0.2s;
  margin-bottom: 6px;
  user-select: none;
}

.field-item:hover {
  background: #1890ff;
  color: #fff;
  border-color: #1890ff;
}

.field-item.field-group-title {
  background: #fff7e6;
  border-color: #ffd591;
  color: #fa8c16;
  font-weight: 500;
}

.field-item.field-group-title:hover {
  background: #fa8c16;
  color: #fff;
  border-color: #fa8c16;
}

/* 右侧设计器容器 */
.designer-container {
  flex: 1;
  overflow: auto;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #f0f2f5;
}

.designer-canvas {
  width: 100%;
  min-height: 500px;
}

/* 右侧属性面板 */
.property-panel {
  width: 280px;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  overflow-y: auto;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.property-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  font-weight: 500;
  background: #fafafa;
}

.property-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.property-content .ant-form-item {
  margin-bottom: 12px;
}

.property-content .ant-form-item-label {
  padding-bottom: 2px;
}

.property-content .ant-form-item-label > label {
  font-size: 12px;
  color: #666;
}

/* hiprint 设计器样式覆盖 */
.designer-canvas >>> .hiprint-designer {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
}

.designer-canvas >>> .hiprint-printPanel {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin: 10px auto;
}

/* 预览样式 */
.preview-toolbar {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;
}

.preview-content {
  max-height: 600px;
  overflow: auto;
  background: #fff;
  padding: 20px;
}

.preview-html {
  width: 100%;
}

.preview-html >>> .hiprint-printPanel {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin: 10px auto;
}
</style>
